// src/components/ui/StripeElements.js
import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Mock Stripe key - replace with real key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_mock_key');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentForm = ({ onSubmit, loading, user }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // For now, just call the onSubmit with mock data
      // TODO: Replace with real Stripe integration
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: user?.email?.split('@')[0] || 'User',
          email: user?.email,
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        onSubmit({
          paymentMethodId: result.paymentMethod.id,
          paymentMethod: result.paymentMethod
        });
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="border border-gray-300 rounded-lg p-3">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm flex items-center">
          <span className="mr-2">⚠️</span>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing || loading}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {processing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Processing...
          </>
        ) : (
          'Add Payment Method'
        )}
      </button>
    </form>
  );
};

const StripeElementsWrapper = ({ onSubmit, loading, user }) => {
  const [stripeLoading, setStripeLoading] = useState(true);

  React.useEffect(() => {
    // Simulate Stripe loading
    const timer = setTimeout(() => {
      setStripeLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (stripeLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if Stripe is properly configured
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  
  if (!stripeKey || stripeKey === 'pk_test_mock_key') {
    return (
      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
        <div className="text-gray-600 mb-2">
          <span className="text-2xl">🔧</span>
        </div>
        <p className="text-gray-600 text-sm mb-2">
          Stripe integration ready for setup
        </p>
        <p className="text-xs text-gray-500">
          Add REACT_APP_STRIPE_PUBLISHABLE_KEY to your environment variables
        </p>
        <button
          onClick={() => onSubmit({
            paymentMethodId: `pm_mock_${Date.now()}`,
            paymentMethod: {
              id: `pm_mock_${Date.now()}`,
              card: {
                last4: '4242',
                exp_month: 12,
                exp_year: 2025,
                brand: 'visa'
              },
              billing_details: {
                name: user?.email?.split('@')[0] || 'User'
              }
            }
          })}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Add Mock Payment Method
        </button>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm onSubmit={onSubmit} loading={loading} user={user} />
    </Elements>
  );
};

export default StripeElementsWrapper;










